import { useIntl } from 'react-intl';
import { foundation } from '../../../ui';
import MfaOtpRow from './MfaOtpRow';
import { mfaStepTitle, mfaStepSub } from './mfaStyles';

const f = foundation;
const fc = f.color;

const messageStyle = (isError) => ({ minHeight: 22, textAlign: 'center', fontSize: f.fontSize.sm, fontFamily: f.fontFamily.sans, marginTop: f.spacePx[2], color: isError ? fc.semantic.danger : fc.semantic.success, fontWeight: isError ? f.fontWeight.medium : f.fontWeight.semibold });
const linkStyle = { marginLeft: f.spacePx[1], border: 'none', background: 'transparent', color: fc.brand.text, fontWeight: f.fontWeight.semibold, fontSize: f.fontSize.sm, fontFamily: f.fontFamily.sans, cursor: 'pointer', padding: 0, textDecoration: 'underline' };

/**
 * RD-SEC-015 flow A step 2 / flow F — the 6-digit code entry. `fromApp`
 * (set once the iPhone handoff — flow G — detects a return from the
 * authenticator app) swaps the subtitle to the "code is above the keyboard"
 * copy. `missCount` drives the hint ladder: 1 miss → a specific hint, 2+ →
 * the same hint plus a "scan again" link back to the add step.
 *
 * RD-SEC-015 (2026-09-25 anatomy fix): the "Verify" CTA now lives in the
 * caller's PINNED footer, not inline here. `onVerify` fires on Enter and,
 * since RD-SEC-019, when a WHOLE code arrives at once (paste / autofill);
 * typing digit-by-digit never auto-submits. `title` is optional
 * (default = the enrollment copy). `sideways` (the pop-up on a phone held sideways, KAN-1012)
 * tightens the title / subtitle gaps and uses 44px code boxes; the forced-enrollment gates never
 * pass it.
 */
export default function MfaStepCode({
  code, onChange, status, missCount, busy, fromApp, title, onVerify, onRescan, sideways = false,
}) {
  const intl = useIntl();

  let message = null;
  if (status === 'verified') {
    message = <div style={messageStyle(false)} aria-live="polite">{intl.formatMessage({ id: 'mfaStepper.code.ok', defaultMessage: 'Verified' })}</div>;
  } else if (missCount >= 2) {
    message = (
      <div style={messageStyle(true)} aria-live="polite">
        {intl.formatMessage({ id: 'mfaStepper.code.err2', defaultMessage: 'Still failing? Check it’s the “CrewVex” entry.' })}
        <button type="button" style={linkStyle} onClick={onRescan}>
          {intl.formatMessage({ id: 'mfaStepper.code.rescan', defaultMessage: 'Scan again' })}
        </button>
      </div>
    );
  } else if (missCount === 1) {
    message = <div style={messageStyle(true)} aria-live="polite">{intl.formatMessage({ id: 'mfaStepper.code.err1', defaultMessage: 'That code doesn’t match. Use the current one.' })}</div>;
  } else {
    message = <div style={messageStyle(false)} aria-live="polite" />;
  }

  return (
    <div>
      <p style={mfaStepTitle(sideways)}>{title ?? intl.formatMessage({ id: 'mfaStepper.code.title', defaultMessage: 'Enter the code' })}</p>
      <p style={mfaStepSub(sideways)}>
        {fromApp
          ? intl.formatMessage({ id: 'mfaStepper.code.subBack', defaultMessage: 'Tap the code above the keyboard, or type it.' })
          : intl.formatMessage({ id: 'mfaStepper.code.sub', defaultMessage: 'It’s under “CrewVex” in your app.' })}
      </p>
      <MfaOtpRow
        sideways={sideways}
        value={code}
        onChange={onChange}
        disabled={busy}
        status={status}
        onSubmit={(digits) => onVerify(digits)}
        onWholeCode={(digits) => onVerify(digits)}
        groupLabel={intl.formatMessage({ id: 'mfaStepper.code.title', defaultMessage: 'Enter the code' })}
        digitLabel={(n) => intl.formatMessage({ id: 'auth.digitAria', defaultMessage: 'Digit {n} of 6' }, { n })}
      />
      {message}
    </div>
  );
}
